import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, IconButton, Popover, Box, Typography, Button, Chip,
} from '@mui/material';
import {
  NotificationsNone, Warning, Error as ErrorIcon, Info, CheckCircle,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { API_ENDPOINTS } from '../../config/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND, STATE_COLORS } from '../../theme/colors';

const SEVERITY_CONFIG = {
  CRITICAL: { icon: <ErrorIcon sx={{ fontSize: 18 }} />, color: '#d32f2f' },
  HIGH: { icon: <Warning sx={{ fontSize: 18 }} />, color: '#ed6c02' },
  MEDIUM: { icon: <Info sx={{ fontSize: 18 }} />, color: '#1976d2' },
  LOW: { icon: <CheckCircle sx={{ fontSize: 18 }} />, color: BRAND.green },
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.ALERTS_ACTIVE, { params: { lang: language } });
      const data = res.data?.alerts || res.data || [];
      // What a farmer sees is decided by the backend (alerts/farmer_alerts.py):
      // the «Άρδευση τώρα» alert and his own messages; equipment is staff-only.
      const filtered = Array.isArray(data) ? data : [];
      setAlerts(filtered);
    } catch {
      // Silently fail
    }
  // isAdmin: the server answers per view (X-TerraMind-View), so a view switch refetches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, isAdmin]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleAcknowledge = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.post(API_ENDPOINTS.ALERT_ACKNOWLEDGE(id));
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch {
      // Silently fail
    }
  };

  const open = Boolean(anchorEl);
  const count = alerts.length;
  const recent = alerts.slice(0, 5);

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={count > 0
          ? `${t('alerts') || 'Alerts'} · ${count} ${t('active') || 'active'}`
          : (t('noActiveAlerts') || 'No active alerts')}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ color: count > 0 ? '#d32f2f' : STATE_COLORS.UNKNOWN }}
      >
        <Badge
          badgeContent={count}
          color="error"
          max={99}
          overlap="circular"
          // Hidden while the presentation runs: "99+" over the bell reads as
          // an operations problem at a sales stand, not a feature.
          invisible={typeof document !== 'undefined'
            && document.body.getAttribute('data-showcase') === 'on'}
        >
          <NotificationsNone sx={{ fontSize: 22 }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              // 420px on desktop, but clamp to viewport so the popover
              // doesn't overflow the right edge on phones.
              width: { xs: 'min(calc(100vw - 16px), 420px)', sm: 420 },
              maxHeight: 480, borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
            {t('alerts') || 'Alerts'}
          </Typography>
          {count > 0 && (
            <Chip label={count} size="small" color="error" sx={{ height: 24, fontSize: '0.9rem', fontWeight: 700 }} />
          )}
        </Box>

        {/* Alert items */}
        <Box sx={{ overflow: 'auto', maxHeight: 300 }}>
          {recent.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 32, color: BRAND.green, opacity: 0.4, mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {t('noActiveAlerts') || 'No active alerts'}
              </Typography>
            </Box>
          ) : (
            recent.map((alert) => {
              const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.MEDIUM;
              const hasField = alert.field != null;
              const goToField = () => {
                setAnchorEl(null);
                navigate(alert.zone ? `/fields/${alert.field}?zone=${alert.zone}` : `/fields/${alert.field}`);
              };
              return (
                <Box
                  key={alert.id}
                  onClick={hasField ? goToField : undefined}
                  role={hasField ? 'button' : undefined}
                  tabIndex={hasField ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (hasField && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      goToField();
                    }
                  }}
                  aria-label={hasField
                    ? `${alert.localized_title || alert.title || ''} · ${t('viewFieldOnMapFromAlert') || 'View field'}`
                    : undefined}
                  sx={{
                    px: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.04)',
                    display: 'flex', gap: 1, alignItems: 'flex-start',
                    cursor: hasField ? 'pointer' : 'default',
                    minHeight: 44,                    // mobile touch target
                    '&:hover': { bgcolor: hasField ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.02)' },
                  }}
                >
                  <Box sx={{ color: cfg.color, mt: 0.25, flexShrink: 0 }}>{cfg.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.4 }}>
                      {alert.localized_title || alert.title || alert.message || t('alert') || 'Alert'}
                    </Typography>
                    {alert.field_name && (
                      <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        {alert.field_name}
                        {alert.zone_number != null && ` Z${alert.zone_number}`}
                      </Typography>
                    )}
                    <Typography sx={{ display: 'block', color: STATE_COLORS.UNKNOWN, fontSize: '0.9rem' }}>
                      {alert.created_at ? new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : ''}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={(e) => handleAcknowledge(alert.id, e)}
                    sx={{ minWidth: 0, px: 1.5, py: 0.5, fontSize: '0.9rem', fontWeight: 600, color: BRAND.green, flexShrink: 0 }}
                  >
                    {t('accept') || 'Accept'}
                  </Button>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        {count > 0 && (
          <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <Button
              fullWidth size="small"
              onClick={() => { setAnchorEl(null); navigate('/alerts'); }}
              sx={{ py: 1, fontWeight: 600, fontSize: '0.9rem' }}
            >
              {t('viewAllAlerts') || 'View all alerts'} ({count})
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
